import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    computeDeviceHealth,
    useLazyGetDeviceByLongIdNapiQuery,
} from 'store/services';
import { Device } from 'types';
import { useAuth } from 'hooks';
import { LoadingSpinner } from 'components/data/LoadingSpinner';
import { DeviceInformation } from './views/DeviceInformation/DeviceInformation';
import { DeviceConfig } from './views/DeviceConfig/DeviceConfig';
import { DeviceAlertsConfig } from './views/DeviceAlertsConfig/DeviceAlertsConfig';
import { DeviceUsers } from './views/DeviceUsers/DeviceUsers';
import { DeviceFrequency } from './views/DeviceFrequency/DeviceFrequency';
import { DeviceIssues } from './views/DeviceIssues/DeviceIssues';
import { DeviceDeliveries } from './views/DeviceDeliveries/DeviceDeliveries';
import { DeviceReadings } from './views/DeviceReadings/DeviceReadings';
import { DeviceTests } from './views/DeviceTests/DeviceTests';
import { DeviceHealth } from './views/DeviceHealth/DeviceHealth';
import { DeviceAudit } from './views/DeviceAudit/DeviceAudit';
import { DeviceDeployment } from './views/DeviceDeployment/DeviceDeployment';
import { setViewAsSupplier } from 'store/features/viewAsSupplier';
import { useAppDispatch } from 'store';
import {
    Alarm,
    Assignment,
    BubbleChart,
    Info,
    LocalShipping,
    MonitorHeart,
    NetworkPing,
    People,
    PropaneTank,
    Settings,
    VerifiedUser,
    Wifi,
} from '@mui/icons-material';
import { TabContext } from '@mui/lab';
import { toLower } from 'lodash';
import { TuTabList } from 'components/common/TuTabList/TuTabList';
import { TuTab } from 'components/common/TuTab/TuTab';
import styles from './DeviceDetails.module.scss';

export function DeviceDetails() {
    const { user } = useAuth();
    const params = useParams();
    const dispatch = useAppDispatch();
    const [query, { data, isLoading, isError }] =
        useLazyGetDeviceByLongIdNapiQuery();
    const [device, setDevice] = useState<Device | undefined>(undefined);
    const [currentView, setCurrentView] = useState<string>(
        params.tabName ? toLower(params.tabName) : 'general'
    );
    const [viewsList, setViewsList] = useState<any[]>([]);

    useEffect(() => {
        if (params.id && user) {
            query({
                longDeviceId: params.id ?? '',
            }).then(({ data, isSuccess }) => {
                if (isSuccess) {
                    if (data) {
                        const device = { ...data };
                        if (device) computeDeviceHealth(device);
                        setDevice(device);
                    }
                }
            });
        }
    }, [params.id, query, user]);

    // Set global viewAsSupplier to supplier who owns current device.
    useEffect(() => {
        if (device && device.protected?.supplierId) {
            dispatch(setViewAsSupplier(device.protected?.supplierId));
        }
    }, [device, dispatch]);

    const viewDisplay = device && {
        general: <DeviceInformation user={user} device={device} />,
        config: <DeviceConfig device={device} setDevice={setDevice} />,
        alerts: (
            <DeviceAlertsConfig
                device={device}
                user={user}
                setDevice={setDevice}
            />
        ),
        users: <DeviceUsers device={device} user={user} />,
        issues: <DeviceIssues device={device} />,
        deliveries: <DeviceDeliveries device={device} />,
        frequency: <DeviceFrequency device={device} />,
        readings: (
            <ProtectedTab
                setCurrentView={setCurrentView}
                Tab={<DeviceReadings device={device} user={user} />}
            />
        ),
        test: (
            <ProtectedTab
                setCurrentView={setCurrentView}
                Tab={<DeviceTests device={device} setDevice={setDevice} />}
            />
        ),
        health: (
            <ProtectedTab
                setCurrentView={setCurrentView}
                Tab={<DeviceHealth device={device} />}
            />
        ),
        deployment: (
            <ProtectedTab
                setCurrentView={setCurrentView}
                Tab={<DeviceDeployment device={device} />}
            />
        ),
        audit: (
            <ProtectedTab
                setCurrentView={setCurrentView}
                Tab={<DeviceAudit device={device} />}
            />
        ),
    };
    const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
        setCurrentView(newValue);
        window.history.replaceState(
            null,
            '',
            `/device-details/${params.id}/${newValue}`
        );
    };

    // From what I can see, in ops-portal we look for supplierId === 0 to show admin-only tabs
    // My supplierId when logged into an admin account here was null, not sure why
    useEffect(() => {
        const tabs = [
            {
                label: 'General',
                value: 'general',
                icon: <Info />,
            },
            {
                label: 'Config',
                value: 'config',
                icon: <Settings />,
            },
            {
                label: 'Alerts',
                value: 'alerts',
                icon: <Alarm />,
            },
            {
                label: 'Users',
                value: 'users',
                icon: <People />,
            },
            {
                label: 'Issues',
                value: 'issues',
                icon: <Assignment />,
            },
            {
                label: 'Deliveries',
                value: 'deliveries',
                icon: <LocalShipping />,
            },
            {
                label: 'Frequency',
                value: 'frequency',
                icon: <Wifi />,
            },
        ];

        const adminTabs = [
            {
                label: 'Readings',
                value: 'readings',
                icon: <BubbleChart />,
            },
            {
                label: 'Test',
                value: 'test',
                icon: <NetworkPing />,
            },
            {
                label: 'Health',
                value: 'health',
                icon: <MonitorHeart />,
            },
            {
                label: 'Deployment',
                value: 'deployment',
                icon: <PropaneTank />,
            },
            {
                label: 'Audit',
                value: 'audit',
                icon: <VerifiedUser />,
            },
        ];
        if (user?.protected?.supplierId) setViewsList(tabs);
        else if (user?.protected?.supplierId?.toString() === '0')
            setViewsList([...tabs, ...adminTabs]);
    }, [user?.protected?.supplierId]);

    return (
        <div data-testid="DeviceDetails" className={styles.container}>
            <TabContext value={currentView}>
                <TuTabList
                    orientation="horizontal"
                    onChange={handleTabChange}
                    variant="scrollable"
                >
                    {viewsList.map((tab) => (
                        <TuTab key={tab.value} {...tab} />
                    ))}
                </TuTabList>
            </TabContext>
            <div className={styles.lowerContainer}>
                {isLoading && <LoadingSpinner />}
                {viewDisplay && viewDisplay[currentView as keyof object]}
                {!data && isError && !isLoading && (
                    <div data-testid="DeviceDetails-error">
                        Monitor Not Found
                    </div>
                )}
            </div>
        </div>
    );
}

interface ProtectedTabProps {
    setCurrentView: React.Dispatch<React.SetStateAction<string>>;
    Tab: JSX.Element;
}

function ProtectedTab({ setCurrentView, Tab }: ProtectedTabProps) {
    const { user } = useAuth();
    const params = useParams();

    const navigateUser = () => {
        window.history.replaceState(
            null,
            '',
            `/device-details/${params.id}/general`
        );
    };

    const userSupplierId = user?.protected?.supplierId;

    if (userSupplierId?.toString() !== '0') {
        setCurrentView('general');
        navigateUser();
    }

    return Tab;
}
